import FileUpload from 'components/hoc/FileUpload';
import DiscussionSpeechBubble from 'components/Icons/DiscussionSpeechBubble';
import Upload from 'components/Icons/Upload';
import BaseTextarea from 'components/inputs/BaseTextarea';
import OrderedSetMultiSelect from 'components/inputs/dropdowns/OrderedSetMultiSelect';
import Label from 'components/inputs/Label';
import IconButton from 'components/lib/IconButton';
import { InsertFilesSubmitParams } from 'components/modals/InsertFilesModal/InsertFilesModal';
import { useIsPendoEnabled } from 'hooks/useIsPendoEnabled';
import { List, OrderedSet } from 'immutable';
import React from 'react';
import { ProjectDriveSelectedItem } from 'records/ProjectDriveSelectedItem';
import { RFIAttachment } from 'records/RFIAttachment';
import { StudioProjectFileData } from 'records/StudioProjectFile';
import { useTranslator } from '@bluebeam/react-i18n';
import { IntegrationsSelectedItem } from 'records/IntegrationsSelectedItem';
import InsertFilesButton from '../InsertFilesButton';
import RFIFilesList from '../RFIFilesList';
import { useViewRfiMachine, ViewRFIEventsEnum } from './ViewRFIMachine';
import {
  AttachmentTypeEnum,
  ForwardMessage,
  RemoveAttachmentTypes,
  RFIResponse,
} from './ViewRFIMachine/viewRfiMachineTypes';

type Props = {
  response: RFIResponse | ForwardMessage;
  recipients?: OrderedSet<string>;
  title?: string;
  canAccessDiscussionModal?: boolean;
};

const ForwardForm: React.FC<Props> = ({
  recipients = OrderedSet<string>(),
  canAccessDiscussionModal = false,
  title,
  response,
}) => {
  const { isPendoEnabled } = useIsPendoEnabled();
  const { viewRfiMachineService, viewRfiMachineState } = useViewRfiMachine();
  const {
    rfi: { discussionattachmentids, forwardactivity },
  } = viewRfiMachineState.context;

  const discussionAttachments = forwardactivity.filter((a) =>
    discussionattachmentids.has(a.id)
  );

  const drawingAttachments = response.attachments.DRAWING;
  const fileAttachments = response.attachments.FILE;
  const studioAttachments = response.attachments.STUDIO;
  const driveAttachments = response.attachments.DRIVE;
  const integrationAttachments = response.attachments.INTEGRATION;

  const { translate } = useTranslator(['formLabels', 'buttons']);

  const hasAttachments =
    drawingAttachments.size > 0 ||
    fileAttachments.size > 0 ||
    studioAttachments.size > 0 ||
    driveAttachments.size > 0 ||
    integrationAttachments.size > 0 ||
    discussionAttachments.size > 0;

  const handleRFIMessageChange = (val: string): void => {
    viewRfiMachineService.send(ViewRFIEventsEnum.UPDATE, {
      data: { value: val },
    });
  };

  const handleRFIForwardListChange = (val: OrderedSet<string>): void => {
    viewRfiMachineService.send(ViewRFIEventsEnum.UPDATE_FORWARD_LIST, {
      data: { forwardList: val },
    });
  };

  const handleDiscussionAttachmentRemove = (id: string) => {
    viewRfiMachineService.send({
      type: ViewRFIEventsEnum.DELETE_DISCUSSION_ATTACHMENT,
      data: { id },
    });
  };

  const showDiscussionAttachments = () => {
    viewRfiMachineService.send(ViewRFIEventsEnum.VIEW_ATTACHED_DISCUSSION);
  };

  const handleUpdateAttachmment = (
    attachment: FileList | InsertFilesSubmitParams
  ) => {
    if (attachment instanceof FileList) {
      viewRfiMachineService.send({
        type: ViewRFIEventsEnum.UPDATE_ATTACHMENT,
        data: { item: attachment, source: AttachmentTypeEnum.FILE },
      });
    } else {
      if (isPendoEnabled && attachment.projectDriveSelectedItems.count() > 0) {
        pendo.track('Added RFI Attachment from Integrations');
      }
      if (isPendoEnabled && attachment.integrationsSelectedItems.count() > 0) {
        pendo.track('Added RFI Attachment from Account Integrations');
      }
      viewRfiMachineService.send([
        {
          type: ViewRFIEventsEnum.UPDATE_ATTACHMENT,
          data: {
            item: attachment.projectDriveSelectedItems,
            source: AttachmentTypeEnum.DRIVE,
          },
        },
        {
          type: ViewRFIEventsEnum.UPDATE_ATTACHMENT,
          data: {
            item: attachment.integrationsSelectedItems,
            source: AttachmentTypeEnum.INTEGRATION,
          },
        },
        {
          type: ViewRFIEventsEnum.UPDATE_ATTACHMENT,
          data: {
            item: OrderedSet(attachment.selectedPages),
            source: AttachmentTypeEnum.DRAWING,
          },
        },
        {
          type: ViewRFIEventsEnum.UPDATE_ATTACHMENT,
          data: {
            item: attachment.studioItems,
            meta: attachment.studioProjectData,
            source: AttachmentTypeEnum.STUDIO,
          },
        },
      ]);
    }
  };

  const handleRemoveAttachmment = (
    attachment:
      | RFIAttachment
      | File
      | string
      | ProjectDriveSelectedItem
      | IntegrationsSelectedItem
      | StudioProjectFileData,
    source: AttachmentTypeEnum
  ) => {
    viewRfiMachineService.send({
      type: ViewRFIEventsEnum.REMOVE_ATTACHMENT,
      data: {
        item: attachment,
        source,
      },
    });
  };

  return (
    <>
      {recipients.size > 0 && (
        <div className="mb3">
          <Label
            label={translate('formLabels:forwardTo')}
            forAttribute="view-rfi-forward-list-input"
            required
          />
          <OrderedSetMultiSelect
            id="forward-rfi-overlay-forward-list-select"
            inputId="view-rfi-forward-list-input"
            placeholder={translate('formLabels:selectWithDots')}
            options={recipients}
            values={(response as ForwardMessage).forwardList}
            onChange={(nextValues): void =>
              handleRFIForwardListChange(nextValues)
            }
          />
        </div>
      )}
      <div className="mb3">
        <Label
          label={title || translate('formLabels:message')}
          forAttribute="forward-rfi-overlay-reponse-textarea"
          required
        />
        <BaseTextarea
          id="forward-rfi-overlay-reponse-textarea"
          className="w100"
          value={response?.message}
          onChange={({
            target: { value },
          }: React.ChangeEvent<HTMLTextAreaElement>): void =>
            handleRFIMessageChange(value)
          }
        />
      </div>
      <div className="mb3">
        <div className="w100 row justify-between">
          <InsertFilesButton
            id="forward-rfi-overlay-attach-drawings-btn"
            initialPages={drawingAttachments}
            initialProjectDriveItems={driveAttachments}
            initialIntegrationItems={integrationAttachments}
            initialStudioItems={studioAttachments}
            onSubmit={handleUpdateAttachmment}
            className="grow-1 ws2"
          />
          <div className="ws1" />
          <FileUpload
            id="forward-rfi-overlay-attach-document"
            accept=""
            onFileSelect={(addFiles): void => handleUpdateAttachmment(addFiles)}
          >
            {({ openFilePicker }): React.ReactNode => (
              <IconButton
                variant="secondary"
                isLoading={false}
                size="small"
                onClick={openFilePicker}
                className="flex-center grow-1 ws2"
              >
                <Upload />
                {translate('buttons:uploadFiles')}
              </IconButton>
            )}
          </FileUpload>
        </div>
        {canAccessDiscussionModal && (
          <div className="w50 row pr2 mt3">
            <IconButton
              variant="secondary"
              isLoading={false}
              size="large"
              onClick={showDiscussionAttachments}
              className="flex-center grow-1 ws2"
            >
              <div className="w20 h50">
                <DiscussionSpeechBubble width="20px" height="20px" />
              </div>
              {translate('buttons:discussion')}
            </IconButton>
          </div>
        )}
      </div>
      {hasAttachments && (
        <RFIFilesList
          files={fileAttachments}
          sheetVersions={response.attachments.drawingSheetVersions}
          attachments={List<RFIAttachment>()}
          projectDriveItems={driveAttachments}
          integrationItems={integrationAttachments}
          studioItems={studioAttachments}
          onAttachmentRemove={(
            attachment: RemoveAttachmentTypes,
            source: AttachmentTypeEnum
          ) => handleRemoveAttachmment(attachment, source)}
          discussionAttachments={discussionAttachments}
          onDiscussionAttachmentRemove={handleDiscussionAttachmentRemove}
        />
      )}
    </>
  );
};

export default ForwardForm;
